import Button from '@hellodarwin/core/lib/components/common/button';
import Modal from '@hellodarwin/core/lib/components/common/hd-modal';
import List from '@hellodarwin/core/lib/components/common/List';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import GrantNotificationCard from '@hellodarwin/core/lib/components/notifications/card';
import {
  GrantNotificationEmptySection,
  GrantNotificationListContainer,
} from '@hellodarwin/core/lib/components/notifications/card/styles';
import DeleteJourneyModal from '@hellodarwin/core/lib/components/notifications/delete-journey-modal';
import FrequencyJourneyModal from '@hellodarwin/core/lib/components/notifications/frequency-journey-modal';
import GrantNotificationListHeader from '@hellodarwin/core/lib/components/notifications/notification-list-header';
import PauseJourneyModal from '@hellodarwin/core/lib/components/notifications/pause-journey-modal';
import { NotificationPreferencesCardContainer } from '@hellodarwin/core/lib/components/notifications/preferences/styles';
import {
  NotificationFrequency,
  NotificationSubscriptionWithSubjectInfo,
} from '@hellodarwin/core/lib/features/entities';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import AddNew from '@hellodarwin/icons/dist/icons/AddNew';
import { Empty } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../app/app-hooks';
import {
  fetchSubscribedNotifications,
  selectAllSubscribedNotifications,
  selectSubscribedNotificationLoading,
  subscribeNotifications,
  unsubscribeNotifications,
} from '../../features/api/slices/grants-notifications-slice';
import { selectContact } from '../../features/api/slices/profile-slice';
import { useNewClientApi } from '../../features/api/use-client-api';
import {
  NotificationsActions,
  NotificationsBody,
} from './styles';
import AlertPreferences from './alert-preferences';

const GrantAlertSettings = () => {
  const dispatch = useAppDispatch();
  const newApi = useNewClientApi();
  const { t } = useTranslations();
  const notifications = useAppSelector(selectAllSubscribedNotifications);
  const notificationsAreLoading = useAppSelector(
    selectSubscribedNotificationLoading,
  );
  const { selectedLocale } = useLocale();
  const navigate = useNavigate();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [frequencyModalVisible, setFrequencyModalVisible] = useState(false);
  const [pauseModalVisible, setPauseModalVisible] = useState(false);
  const [activeNotification, setActiveNotification] =
    useState<NotificationSubscriptionWithSubjectInfo | null>(null);
  const contact = useAppSelector(selectContact);
  const theme = useTheme();

  const orderedGrantNotifications = useMemo(
    () =>
      notifications.filter(
        (notification) => notification.object_type === 'grant',
      ),
    [notifications],
  );

  useEffect(() => {
    dispatch(
      fetchSubscribedNotifications({ api: newApi, locale: selectedLocale }),
    );
  }, [dispatch, newApi, selectedLocale]);

  const handleDeleteSubscription = async (
    grantId: string,
    industryId: string,
    tagId: string,
  ) => {
    if (!grantId && !industryId && !tagId) return;
    await dispatch(
      unsubscribeNotifications({
        api: newApi,
        request: {
          contact_id: contact.contact_id,
          grant_ids: grantId ? [grantId] : [],
          industry_ids: industryId ? [industryId] : [],
          tag_ids: tagId ? [tagId] : [],
        },
      }),
    );
    setDeleteModalVisible(false);
  };

  const handleNotificationUpdate = async (
    grantId: string,
    industryId: string,
    tagId: string,
    frequency?: NotificationFrequency,
    pausedUntil?: Date | null,
  ) => {
    if (!grantId && !industryId && !tagId) return;
    await dispatch(
      subscribeNotifications({
        api: newApi,
        request: {
          contact_id: contact.contact_id,
          grant_ids: grantId ? [grantId] : [],
          industry_ids: industryId ? [industryId] : [],
          tag_ids: tagId ? [tagId] : [],
          frequency,
          paused_until: pausedUntil,
        },
      }),
    );
    setDeleteModalVisible(false);
  };

  const navigateToSubject = (subjectId: string, objectType: string) => {
    switch (objectType) {
      case 'grant':
        navigate(`/funding-explorer/${subjectId}`);
        break;
      case 'industry':
        navigate(`/funding-explorer?industry=${subjectId}`);
        break;
      case 'tag':
        navigate(`/funding-explorer?goalsTags=${subjectId}`);
        break;
      default:
        navigate('/funding-explorer');
    }
  };

  return (
    <>
      <AlertPreferences />
      <NotificationPreferencesCardContainer>
        <GrantNotificationListContainer>
          <NotificationsActions>
            <Typography elementTheme="h6">
              {t('notification|grantNotifications')}
            </Typography>
            <Button
              defaultStyle={theme.colors.purple_1}
              trailingIcon={<AddNew size={20} />}
              onClick={() => navigate('/funding-explorer')}
            >
              <Typography elementTheme="body1">
                {t('notification|addAlerts')}
              </Typography>
            </Button>
          </NotificationsActions>
          {orderedGrantNotifications.length > 0 && (
            <NotificationsBody>
              <GrantNotificationListHeader />
              <List
                renderEmpty={() => (
                  <Empty
                    description={t(
                      'grantNotificationJourneys|noNotificationsForContact',
                    )}
                  />
                )}
                loading={notificationsAreLoading}
                dataSource={orderedGrantNotifications}
                renderItem={(
                  notification: NotificationSubscriptionWithSubjectInfo,
                ) => (
                  <GrantNotificationCard
                    {...notification}
                    handleDeleteJourney={() => {
                      setActiveNotification(notification);
                      setDeleteModalVisible(true);
                    }}
                    handleNavigateToSubject={() =>
                      navigateToSubject(
                        notification.grant_id ?? '',
                        notification.object_type,
                      )
                    }
                    handlePauseResume={(isPaused: boolean) => {
                      if (isPaused) {
                        void handleNotificationUpdate(
                          notification.grant_id ?? '',
                          notification.industry_id ?? '',
                          notification.tag_id ?? '',
                          undefined,
                          null,
                        );
                      } else {
                        setActiveNotification(notification);
                        setPauseModalVisible(true);
                      }
                    }}
                  />
                )}
                styles={{ content: { gap: theme.spacing.sm } }}
              />
            </NotificationsBody>
          )}

        </GrantNotificationListContainer>
      </NotificationPreferencesCardContainer>

      {orderedGrantNotifications.length === 0 && (
        <GrantNotificationEmptySection>
          <Typography elementTheme="body1" textAlign="center">
            {t('notification|noNotifications')}
          </Typography>
          <Button to="/funding-explorer">
            {t('notification|noNotificationsButton')}
          </Button>
        </GrantNotificationEmptySection>
      )}

      {deleteModalVisible && activeNotification && (
        <Modal
          open={deleteModalVisible}
          handleCancel={() => setDeleteModalVisible(false)}
        >
          <DeleteJourneyModal
            notification={activeNotification}
            setDeleteModal={setDeleteModalVisible}
            handleDeleteSubscription={handleDeleteSubscription}
          />
        </Modal>
      )}
      {frequencyModalVisible && activeNotification && (
        <Modal
          open={frequencyModalVisible}
          handleCancel={() => setFrequencyModalVisible(false)}
        >
          <FrequencyJourneyModal
            notification={activeNotification}
            setFrequencyModal={setFrequencyModalVisible}
            handleFrequencyChange={handleNotificationUpdate}
          />
        </Modal>
      )}
      {pauseModalVisible && activeNotification && (
        <Modal
          open={pauseModalVisible}
          handleCancel={() => setPauseModalVisible(false)}
        >
          <PauseJourneyModal
            notification={activeNotification}
            setPauseModal={setPauseModalVisible}
            handlePauseSubscription={handleNotificationUpdate}
          />
        </Modal>
      )}
    </>
  );
};

export default GrantAlertSettings;
